/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { ParameterForm } from '@karya/parameter-specs';

export const GENDER_OPTIONS = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  prefer_not_to_say: 'Prefer not to say',
} as const;

export const PROFESSION_OPTIONS = {
  student: 'Student',
  software_engineer: 'Software Engineer',
  data_scientist: 'Data Scientist',
  teacher: 'Teacher',
  professor: 'Professor',
  doctor: 'Doctor',
  engineer: 'Engineer',
  government_employee: 'Government Employee',
  business_owner: 'Business Owner',
  consultant: 'Consultant',
  freelancer: 'Freelancer',
  researcher: 'Researcher',
  lawyer: 'Lawyer',
  accountant: 'Accountant',
  marketing_professional: 'Marketing Professional',
  hr_professional: 'HR Professional',
  homemaker: 'Homemaker',
  retired: 'Retired',
  other: 'Other',
} as const;

export const LANGUAGE_OPTIONS = {
  english: 'English',
  assamese: 'Assamese',
  bengali: 'Bengali',
  bodo: 'Bodo',
  dogri: 'Dogri',
  gujarati: 'Gujarati',
  hindi: 'Hindi',
  kannada: 'Kannada',
  kashmiri: 'Kashmiri',
  konkani: 'Konkani',
  maithili: 'Maithili',
  malayalam: 'Malayalam',
  manipuri: 'Manipuri (Meitei)',
  marathi: 'Marathi',
  nepali: 'Nepali',
  odia: 'Odia',
  punjabi: 'Punjabi',
  sanskrit: 'Sanskrit',
  santhali: 'Santhali',
  sindhi: 'Sindhi',
  tamil: 'Tamil',
  telugu: 'Telugu',
  urdu: 'Urdu',
} as const;

export const DEGREE_OPTIONS = {
  tenth: '10th / SSLC',
  twelfth: '12th / PUC / Intermediate',
  diploma: 'Diploma',
  bachelors: "Bachelor's Degree",
  btech: 'B.Tech / B.E.',
  mba: 'MBA',
  masters: "Master's Degree",
  mtech: 'M.Tech / M.E.',
  mbbs: 'MBBS',
  phd: 'PhD',
  other: 'Other',
} as const;

export const PROFILE_FORM_SCHEMA: ParameterForm<Record<string, unknown>> = [
  {
    label: 'Basic Details',
    description: 'Please provide your basic information',
    required: true,
    parameters: [
      {
        id: 'first_name',
        label: 'First Name',
        type: 'string',
        required: true,
        placeholder: 'Enter your first name',
        pattern: '^[a-zA-Z\\s]+$',
        maxLength: 50,
      },
      {
        id: 'last_name',
        label: 'Last Name',
        type: 'string',
        required: true,
        placeholder: 'Enter your last name',
        pattern: '^[a-zA-Z\\s]+$',
        maxLength: 50,
      },
      {
        id: 'email',
        label: 'Email',
        type: 'string',
        required: true,
        placeholder: 'Enter your email address',
        pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
        maxLength: 255,
      },
      {
        id: 'date_of_birth',
        label: 'Date of Birth',
        type: 'date',
        required: true,
        description: 'You must be at least 18 years old',
      },
      {
        id: 'age_confirmation',
        label: 'I confirm I am at least 18 years old',
        type: 'boolean',
        required: false,
      },
      {
        id: 'gender',
        label: 'Gender',
        type: 'enum',
        list: GENDER_OPTIONS,
        required: true,
      },
      {
        id: 'pincode',
        label: 'Pincode',
        type: 'string',
        required: true,
        placeholder: 'Enter your pincode',
        pattern: '^[0-9]{6}$',
        maxLength: 6,
      },
    ],
  },
  {
    label: 'Professional Details',
    description: 'Tell us about your professional background',
    required: true,
    parameters: [
      {
        id: 'profession',
        label: 'Profession',
        type: 'enum',
        list: PROFESSION_OPTIONS,
        required: true,
      },
      {
        id: 'languages_written_proficiency',
        label: 'Languages (Written Proficiency)',
        type: 'enum_multi',
        list: LANGUAGE_OPTIONS,
        required: true,
        description: 'Select all languages you can write in',
      },
      {
        id: 'linkedin_profile',
        label: 'LinkedIn Profile',
        type: 'string',
        required: true,
        placeholder: 'Enter your LinkedIn profile URL',
        pattern: '^https?://(www\\.)?linkedin\\.com/in/([a-zA-Z0-9_-]|%[0-9A-Fa-f]{2})+/?$',
        maxLength: 200,
      },
    ],
  },
  {
    label: 'Education Details',
    description: 'Optional: Add your educational background (up to 2 degrees)',
    required: false,
    parameters: [
      {
        id: 'latest_degree',
        label: 'Latest Degree',
        type: 'enum',
        list: DEGREE_OPTIONS,
        required: false,
      },
      {
        id: 'latest_degree_college',
        label: 'College/University',
        type: 'string',
        required: false,
        placeholder: 'Enter college name',
        maxLength: 200,
      },
      {
        id: 'latest_degree_year',
        label: 'Year of Graduation',
        type: 'string',
        required: false,
        placeholder: 'YYYY',
        pattern: '^[0-9]{4}$',
        maxLength: 4,
      },
      {
        id: 'previous_degree',
        label: 'Previous Degree (Optional)',
        type: 'enum',
        list: DEGREE_OPTIONS,
        required: false,
      },
      {
        id: 'previous_degree_college',
        label: 'College/University',
        type: 'string',
        required: false,
        placeholder: 'Enter college name',
        maxLength: 200,
      },
      {
        id: 'previous_degree_year',
        label: 'Year of Graduation',
        type: 'string',
        required: false,
        placeholder: 'YYYY',
        pattern: '^[0-9]{4}$',
        maxLength: 4,
      },
      {
        id: 'resume_cv',
        label: 'Resume/CV',
        type: 'file',
        required: false,
        acceptedTypes: ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
      },
    ],
  },
];
